import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  Mail,
  Phone,
  Trash2,
  Eye,
  EyeOff,
  MessageSquare,
  Star,
  Plus,
  Lock,
  Inbox,
  Quote,
  ImagePlus,
  X,
  Sparkles,
} from "lucide-react";
import { useRef } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "dm_admin_token";

// Resize + compress an image file to a small JPEG data URL for storage
const resizeImage = (file, max = 400) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const api = (token) =>
  axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("pt-PT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const inputCls =
  "bg-white border-[#E6DFD8] rounded-xl h-11 px-4 text-[#3A3532] focus-visible:ring-[#C17F74]/40 focus-visible:border-[#C17F74]";

/* ---------------- login ---------------- */

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token, data.user);
      toast.success("Bem-vinda, Denise!");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao iniciar sessão");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-[2rem] bg-white border border-[#E6DFD8] p-9 shadow-[0_20px_60px_rgb(58,53,50,0.08)]"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F4E8E4] text-[#C17F74] mb-6">
          <Lock size={20} />
        </div>
        <h1 className="font-serif text-3xl text-[#3A3532]">Painel privado</h1>
        <p className="text-sm text-[#756B66] mt-1 mb-7">Área reservada da Denise Maria.</p>
        <label className="block text-sm text-[#756B66] mb-1.5">Email</label>
        <Input data-testid="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        <label className="block text-sm text-[#756B66] mb-1.5 mt-4">Palavra-passe</label>
        <Input data-testid="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading}
          className="mt-7 w-full rounded-full bg-[#C17F74] text-white py-3.5 font-medium transition-[background-color,transform] duration-300 hover:bg-[#A9685E] active:scale-[0.98] disabled:opacity-60"
        >
          {loading ? "A entrar..." : "Entrar"}
        </button>
        <a href="/" className="block text-center text-xs text-[#756B66] mt-5 hover:text-[#C17F74]">
          ← Voltar ao site
        </a>
      </form>
    </div>
  );
};

/* ---------------- contacts tab ---------------- */

const Contacts = ({ token }) => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    api(token)
      .get("/admin/contacts")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Erro ao carregar pedidos"));
  }, [token]);
  useEffect(() => load(), [load]);

  const remove = async (id) => {
    await api(token).delete(`/admin/contacts/${id}`);
    setItems((x) => x.filter((i) => i.id !== id));
    toast.success("Pedido removido");
  };
  const markRead = async (id) => {
    await api(token).patch(`/admin/contacts/${id}/read`);
    setItems((x) => x.map((i) => (i.id === id ? { ...i, read: true } : i)));
  };

  if (!items.length)
    return (
      <div className="text-center text-[#756B66] py-20">
        <Inbox size={40} className="mx-auto text-[#E8C5BE] mb-3" />
        Ainda não há pedidos de contacto.
      </div>
    );

  return (
    <div className="grid gap-4">
      {items.map((c) => (
        <div
          key={c.id}
          data-testid={`contact-row-${c.id}`}
          className={`rounded-2xl border p-6 bg-white transition-colors ${
            c.read ? "border-[#E6DFD8]" : "border-[#C17F74]/50 bg-[#F4E8E4]/30"
          }`}
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-serif text-xl text-[#3A3532]">{c.name}</span>
                {!c.read && <span className="text-[10px] uppercase tracking-wide bg-[#C17F74] text-white rounded-full px-2 py-0.5">Novo</span>}
                <span className="text-[10px] uppercase bg-[#F4E8E4] text-[#756B66] rounded-full px-2 py-0.5">{c.lang}</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#756B66]">
                <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 hover:text-[#C17F74]"><Mail size={13} />{c.email}</a>
                <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 hover:text-[#C17F74]"><Phone size={13} />{c.phone}</a>
              </div>
            </div>
            <span className="text-xs text-[#B5ABA4]">{fmtDate(c.created_at)}</span>
          </div>
          <p className="mt-4 text-[#3A3532] leading-relaxed whitespace-pre-wrap">{c.message}</p>
          <div className="mt-4 flex gap-2">
            {!c.read && (
              <button onClick={() => markRead(c.id)} className="text-xs rounded-full border border-[#E6DFD8] px-3 py-1.5 text-[#756B66] hover:bg-[#F4E8E4]">
                Marcar como lido
              </button>
            )}
            <a href={`https://wa.me/${c.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="text-xs rounded-full bg-[#25D366] text-white px-3 py-1.5 hover:opacity-90">
              WhatsApp
            </a>
            <button data-testid={`delete-contact-${c.id}`} onClick={() => remove(c.id)} className="text-xs rounded-full border border-[#E6DFD8] px-3 py-1.5 text-[#C17F74] hover:bg-[#F4E8E4] flex items-center gap-1">
              <Trash2 size={12} /> Eliminar
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ---------------- testimonials tab ---------------- */

const empty = { author: "", location: "", quote_pt: "", quote_en: "", photo: "", rating: 5 };

const Testimonials = ({ token }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api(token).get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => {});
  }, [token]);
  useEffect(() => load(), [load]);

  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Por favor selecione uma imagem");
      return;
    }
    try {
      const dataUrl = await resizeImage(file, 400);
      setForm((f) => ({ ...f, photo: dataUrl }));
    } catch {
      toast.error("Não foi possível carregar a imagem");
    }
  };

  const doTranslate = async () => {
    const hasPt = form.quote_pt.trim();
    const hasEn = form.quote_en.trim();
    if (!hasPt && !hasEn) {
      toast.error("Escreva o testemunho num dos idiomas primeiro");
      return;
    }
    const target = hasPt ? "en" : "pt";
    const text = hasPt || hasEn;
    setTranslating(true);
    try {
      const { data } = await api(token).post("/admin/translate", { text, target });
      setForm((f) => ({ ...f, [target === "en" ? "quote_en" : "quote_pt"]: data.translated }));
      toast.success(target === "en" ? "Traduzido para Inglês" : "Traduzido para Português");
    } catch {
      toast.error("Falha na tradução automática");
    } finally {
      setTranslating(false);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api(token).post("/admin/testimonials", { ...form, rating: Number(form.rating) });
      setItems((x) => [...x, data]);
      setForm(empty);
      toast.success("Testemunho adicionado");
    } catch {
      toast.error("Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };
  const remove = async (id) => {
    await api(token).delete(`/admin/testimonials/${id}`);
    setItems((x) => x.filter((i) => i.id !== id));
  };
  const toggle = async (id) => {
    const { data } = await api(token).patch(`/admin/testimonials/${id}/visible`);
    setItems((x) => x.map((i) => (i.id === id ? { ...i, visible: data.visible } : i)));
  };

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-8 items-start">
      <form onSubmit={add} data-testid="testimonial-form" className="rounded-2xl border border-[#E6DFD8] bg-white p-6 lg:sticky lg:top-6">
        <h3 className="font-serif text-2xl text-[#3A3532] mb-4 flex items-center gap-2"><Plus size={18} className="text-[#C17F74]" /> Novo testemunho</h3>
        <div className="grid gap-3">
          <Input required placeholder="Nome (ex: Mariana Silva)" value={form.author} onChange={up("author")} className={inputCls} data-testid="t-author" />
          <Input required placeholder="Localidade (ex: Estoril, Lisboa)" value={form.location} onChange={up("location")} className={inputCls} data-testid="t-location" />
          <Textarea required rows={3} placeholder="Testemunho em Português" value={form.quote_pt} onChange={up("quote_pt")} className="bg-white border-[#E6DFD8] rounded-xl px-4 py-3 resize-none focus-visible:ring-[#C17F74]/40" data-testid="t-quote-pt" />
          <Textarea required rows={3} placeholder="Testimonial in English" value={form.quote_en} onChange={up("quote_en")} className="bg-white border-[#E6DFD8] rounded-xl px-4 py-3 resize-none focus-visible:ring-[#C17F74]/40" data-testid="t-quote-en" />
          <button
            type="button"
            onClick={doTranslate}
            disabled={translating}
            data-testid="t-translate"
            className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-[#C17F74] text-[#C17F74] py-2.5 text-sm hover:bg-[#F4E8E4] transition-colors disabled:opacity-60"
          >
            <Sparkles size={15} />
            {translating ? "A traduzir..." : "Traduzir automaticamente o idioma em falta"}
          </button>

          <div>
            <label className="block text-sm text-[#756B66] mb-1.5">Foto (opcional)</label>
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="t-photo-file" />
            {form.photo ? (
              <div className="flex items-center gap-3">
                <img src={form.photo} alt="pré-visualização" className="h-16 w-16 rounded-full object-cover border-2 border-[#F4E8E4]" />
                <button type="button" onClick={() => fileRef.current?.click()} className="text-sm text-[#C17F74] hover:underline">Trocar</button>
                <button type="button" onClick={() => setForm((f) => ({ ...f, photo: "" }))} className="text-sm text-[#756B66] hover:text-[#C17F74] flex items-center gap-1"><X size={13} /> Remover</button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="t-photo-upload"
                className="w-full flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#E6DFD8] py-6 text-[#756B66] hover:border-[#C17F74] hover:text-[#C17F74] transition-colors"
              >
                <ImagePlus size={22} />
                <span className="text-sm">Carregar imagem</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <label className="text-sm text-[#756B66]">Estrelas</label>
            <select value={form.rating} onChange={up("rating")} className="rounded-xl border border-[#E6DFD8] h-11 px-3 bg-white text-[#3A3532]">
              {[5, 4, 3].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
        <button type="submit" disabled={saving} className="mt-5 w-full rounded-full bg-[#C17F74] text-white py-3 font-medium hover:bg-[#A9685E] transition-colors disabled:opacity-60">
          {saving ? "A guardar..." : "Adicionar testemunho"}
        </button>
      </form>

      <div className="grid gap-4">
        {items.length === 0 && <div className="text-[#756B66] py-10 text-center">Sem testemunhos.</div>}
        {items.map((it) => (
          <div key={it.id} className={`rounded-2xl border border-[#E6DFD8] bg-white p-6 ${!it.visible ? "opacity-50" : ""}`}>
            <Quote size={22} className="text-[#E8C5BE]" />
            <p className="mt-2 font-serif text-lg text-[#3A3532]">“{it.quote_pt}”</p>
            <p className="mt-1 text-sm text-[#756B66] italic">“{it.quote_en}”</p>
            <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                {it.photo && <img src={it.photo} alt={it.author} className="h-9 w-9 rounded-full object-cover" />}
                <div>
                  <div className="text-sm font-semibold text-[#3A3532]">{it.author}</div>
                  <div className="text-xs text-[#756B66]">{it.location}</div>
                </div>
                <div className="flex ml-2">{Array.from({ length: it.rating }).map((_, i) => <Star key={i} size={13} className="text-[#C17F74]" fill="#C17F74" />)}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggle(it.id)} className="text-xs rounded-full border border-[#E6DFD8] px-3 py-1.5 text-[#756B66] hover:bg-[#F4E8E4] flex items-center gap-1">
                  {it.visible ? <><EyeOff size={12} /> Ocultar</> : <><Eye size={12} /> Mostrar</>}
                </button>
                <button onClick={() => remove(it.id)} className="text-xs rounded-full border border-[#E6DFD8] px-3 py-1.5 text-[#C17F74] hover:bg-[#F4E8E4] flex items-center gap-1">
                  <Trash2 size={12} /> Eliminar
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------------- root ---------------- */

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("contacts");
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    api(token).get("/auth/me").then(() => setChecking(false)).catch(() => {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setChecking(false);
    });
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (checking) return <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center text-[#756B66]">A carregar...</div>;
  if (!token) return <Login onLogin={(tk) => setToken(tk)} />;

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <header className="sticky top-0 z-10 bg-[#FAF7F2]/85 backdrop-blur-xl border-b border-[#E6DFD8]">
        <div className="mx-auto max-w-[1100px] px-6 h-16 flex items-center justify-between">
          <div>
            <div className="font-serif text-xl text-[#3A3532] leading-none">Denise Maria</div>
            <div className="font-script text-sm text-[#C17F74]">painel de gestão</div>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-sm text-[#756B66] hover:text-[#C17F74] rounded-full border border-[#E6DFD8] px-4 py-2">
            <LogOut size={15} /> Sair
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-[1100px] px-6 py-8">
        <div className="flex gap-2 mb-8">
          {[
            { id: "contacts", label: "Pedidos de contacto", icon: MessageSquare },
            { id: "testimonials", label: "Testemunhos", icon: Star },
          ].map((tb) => {
            const Icon = tb.icon;
            return (
              <button
                key={tb.id}
                data-testid={`tab-${tb.id}`}
                onClick={() => setTab(tb.id)}
                className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm transition-colors ${
                  tab === tb.id ? "bg-[#C17F74] text-white" : "bg-white border border-[#E6DFD8] text-[#756B66] hover:bg-[#F4E8E4]"
                }`}
              >
                <Icon size={15} /> {tb.label}
              </button>
            );
          })}
        </div>
        {tab === "contacts" ? <Contacts token={token} /> : <Testimonials token={token} />}
      </div>
    </div>
  );
}
